/**
 * Capture ad-click attribution (Google click IDs + UTM) from the landing-page URL
 * and stash it in sessionStorage so it survives the navigation between
 * landing → the CRM lead-creation form.
 *
 * Usage:
 *   - Call `captureAdAttributionFromUrl()` once at app boot (or on every
 *     route change — the second call is a no-op if nothing new is in the
 *     URL). Safe to call multiple times.
 *   - Call `readStoredAdAttribution()` inside the lead-creation form to
 *     populate the LeadSource payload on POST.
 *   - Call `clearStoredAdAttribution()` after a successful lead create
 *     so subsequent leads from the same session don't all carry the
 *     same gclid.
 *
 * Why sessionStorage not localStorage: the attribution should tie to the
 * current session's visit chain, not live forever. Also privacy-friendlier
 * — closing the tab clears it.
 */

const STORAGE_KEY = 'adAttribution:v1';
const TRACKED_PARAMS = [
    'gclid',
    'gbraid',
    'wbraid',
    'gad_campaignid',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
];

// Hard cap to defend against pathological URL crafters — Google Ads gclids
// are ~60 chars, real-world UTMs ~128. 512 is generous enough that no
// legitimate value gets truncated, tight enough that a stored-XSS payload
// can't smuggle a full <script>…</script> tag through the utm_* fields.
const MAX_VALUE_LEN = 512;
// Allowlist — printable ASCII minus control chars + common URL-safe bytes.
// Rejects angle brackets, quotes, backticks, newlines. Anything suspicious
// gets dropped rather than escaped so downstream renderers (which may or
// may not be XSS-safe) never have to make that call.
//
// Pipe (|) and tilde (~) are included because some agencies use them as
// separators inside utm_campaign values (e.g. "brand|awareness~uk"). Both
// are URL-safe and non-HTML-dangerous, so widening the allowlist prevents
// legitimate attribution from being silently dropped. Review caught this
// in phase 5 — a dropped utm_* produces a hidden "no attribution" row.
const VALUE_ALLOWED = /^[\w\-.:/?=&+%,@|~ ]*$/;

const _sanitizeValue = (v) => {
    if (typeof v !== 'string') return '';
    const trimmed = v.slice(0, MAX_VALUE_LEN);
    return VALUE_ALLOWED.test(trimmed) ? trimmed : '';
};

function _safeGetSession() {
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

/**
 * Pull Google click IDs + UTM params out of the current URL and persist them.
 * Returns an object of captured fields (empty if none present). Does
 * NOT overwrite existing stored attribution — if a gclid is already
 * stashed and the user navigates to a non-ads URL, the original
 * attribution wins. The store is cleared only by an explicit
 * clearStoredAdAttribution() or by the session expiring.
 */
export function captureAdAttributionFromUrl(location = window.location) {
    const session = _safeGetSession();
    if (!session) return {};
    try {
        const params = new URLSearchParams(location.search || '');
        const captured = {};
        TRACKED_PARAMS.forEach((key) => {
            const raw = params.get(key);
            if (!raw) return;
            const clean = _sanitizeValue(raw);
            // _sanitizeValue returns '' when the value contains characters
            // outside our allowlist. Treat that as "no attribution
            // captured" rather than storing an empty string — otherwise a
            // crafted URL could silently wipe a prior legitimate capture.
            if (clean) captured[key] = clean;
        });
        if (Object.keys(captured).length === 0) return {};
        // Only overwrite if there's nothing there — first attribution wins.
        const existingRaw = session.getItem(STORAGE_KEY);
        if (existingRaw) {
            try {
                const existing = JSON.parse(existingRaw);
                if (existing && (existing.gclid || existing.gbraid || existing.wbraid)) return existing;
            } catch {
                // fall through and overwrite corrupt entry
            }
        }
        session.setItem(STORAGE_KEY, JSON.stringify({
            ...captured,
            captured_at: new Date().toISOString(),
        }));
        return captured;
    } catch {
        return {};
    }
}

/**
 * Read the currently stored attribution (or return an empty object when
 * none is stashed). Safe to call every render.
 */
export function readStoredAdAttribution() {
    const session = _safeGetSession();
    if (!session) return {};
    try {
        const raw = session.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        // Filter down to the fields the backend accepts so we don't
        // accidentally leak captured_at or other metadata.
        const clean = {};
        TRACKED_PARAMS.forEach((k) => {
            if (parsed[k]) clean[k] = parsed[k];
        });
        return clean;
    } catch {
        return {};
    }
}

/**
 * Merge stashed ad attribution (Google click IDs + UTMs) into a lead-creation
 * payload's `lead_source` field — if, and only if, the payload doesn't
 * already carry those fields. Intent is: an explicit `lead_source.gclid`
 * set by the caller always wins; this helper only fills in what's
 * missing. Returns a new payload object — does NOT mutate its input.
 *
 * Usage:
 *   const enriched = mergeAdAttributionIntoLeadPayload(data);
 *   await callApi('/leads/', 'post', enriched);
 *
 * Safe to call even when no attribution is stashed — returns the
 * payload unchanged in that case. The LeadSource serializer accepts
 * these ad-attribution fields as optional; backend drops them
 * gracefully if none are supplied.
 */
export function mergeAdAttributionIntoLeadPayload(data) {
    if (!data || typeof data !== 'object') return data;
    const attribution = readStoredAdAttribution();
    if (Object.keys(attribution).length === 0) return data;

    const existingSource = data.lead_source && typeof data.lead_source === 'object'
        ? data.lead_source
        : {};
    // An explicit click identifies a different attribution bundle. Do not mix
    // session click IDs, campaign or UTMs into that caller-supplied interaction.
    if (['gclid', 'gbraid', 'wbraid'].some((key) => existingSource[key])) return data;
    // Only write keys the caller hasn't set. Importantly, only write when
    // we actually HAVE a captured value — the previous version coerced
    // missing fields to '', which could 400 the POST if the LeadSource
    // serializer rejects empty strings on optional fields. Let the
    // serializer handle absence via its `required=False` flags.
    const nextSource = { ...existingSource };
    TRACKED_PARAMS.forEach((key) => {
        const captured = attribution[key];
        if (!captured) return;
        if (!(key in existingSource) || existingSource[key] === undefined || existingSource[key] === '') {
            nextSource[key] = captured;
        }
    });
    return { ...data, lead_source: nextSource };
}

export function clearStoredAdAttribution() {
    const session = _safeGetSession();
    if (!session) return;
    try {
        session.removeItem(STORAGE_KEY);
    } catch {
        // ignore quota / private-mode errors
    }
}
